import { DeleteOutlined } from '@ant-design/icons'
import ComplementaryItem from '@components/balance/complementary-item/ComplementaryItem'
import { Localizable } from '@components/localizable/Localizable'
import { MembershipProfile } from '@models/membership/MembershipProfile'
import { CurrencyInput } from '@pages/settings/membership/CurrencyInput'
import { batchUpdateMembershipProfileComplementaryItems } from '@services/membership/MembershipProfileComplementaryItemService'
import { batchUpdateMembershipProfileCurrencies } from '@services/membership/MembershipProfileCurrencyService'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  getMembershipProfileComplementaryItems,
  selectMembershipProfileComplementaryItems,
  setMembershipProfileComplementaryItemQuery,
} from '@store/membership/MembershipProfileComplementaryItemReducer'
import {
  getMembershipProfileCurrencies,
  selectMembershipProfileCurrencies,
  setMembershipProfileCurrencyQuery,
} from '@store/membership/MembershipProfileCurrencyReducer'
import { selectMembershipProfileById } from '@store/membership/MembershipProfileReducer'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  message,
  Row,
  Space,
  Typography,
} from 'antd'
import { FC, useEffect, useState } from 'react'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../../App'

const { Title } = Typography

type Props = SBModelComponent<MembershipProfile> & {}

const ComplementaryItemSection: FC<Props> = ({ id, model }) => {
  const [balanceForm] = Form.useForm()
  const [creditForm] = Form.useForm()
  const [isBalanceSaving, setIsBalanceSaving] = useState(false)
  const [isCreditSaving, setIsCreditSaving] = useState(false)
  const { isOpen } = useSBRM()
  const dispatch = useAppDispatch()
  const membershipProfile =
    model ?? useAppSelector(selectMembershipProfileById(id!))
  const currencies = useAppSelector(selectMembershipProfileCurrencies())
  const complementaryItems = useAppSelector(
    selectMembershipProfileComplementaryItems()
  )

  const fetchMembershipProfileDetails = () => {
    if (!membershipProfile) return

    const query: TableParams = {
      ...initialQuery,
      columnKey: 'name',
      order: 'ascend',
      filters: { profiles: [membershipProfile.id] },
    }

    dispatch(setMembershipProfileCurrencyQuery(query))
    dispatch(getMembershipProfileCurrencies(query))
    dispatch(setMembershipProfileComplementaryItemQuery(query))
    dispatch(getMembershipProfileComplementaryItems(query))
  }

  useEffect(() => {
    if (!membershipProfile || isOpen) return

    fetchMembershipProfileDetails()
  }, [membershipProfile, isOpen])

  if (!membershipProfile) {
    return <></>
  }

  return (
    <Localizable>
      <Localizable.LangSelector type="floating" />
      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Form
            form={balanceForm}
            layout="vertical"
            name="balance"
            onFinish={async (values: any) => {
              const amounts = values.amount
              if (!amounts) return

              const items = currencies
                .filter((item) => amounts[item.id] !== undefined)
                .map((item) => ({ id: item.id, amount: amounts[item.id] }))

              if (!items.length) return

              setIsBalanceSaving(true)
              try {
                await batchUpdateMembershipProfileCurrencies(
                  membershipProfile.id,
                  items
                )
                fetchMembershipProfileDetails()
                message.success(DEFAULT_SUCCESS)
              } catch {
                message.error(DEFAULT_ERROR)
              } finally {
                setIsBalanceSaving(false)
              }
            }}
          >
            <Flex vertical className="w-full" gap={8}>
              <Space className="w-full space-between">
                <Title level={5} className="m-0">
                  Balance
                </Title>
                <Space>
                  <Button
                    style={{ border: 0 }}
                    type="primary"
                    htmlType="submit"
                    loading={isBalanceSaving}
                    disabled={isBalanceSaving}
                  >
                    Save
                  </Button>
                </Space>
              </Space>

              <Card>
                {currencies.map((item) => (
                  <Flex key={item.id} align="flex-end" gap={8}>
                    <div style={{ flex: 1 }}>
                      <CurrencyInput entity="membershipProfile" id={item.id} />
                    </div>
                    <SBRM.Actions
                      actions={[SBRMAction.Delete]}
                      entity={Entity.membershipProfileCurrency}
                      entityId={item.id}
                      trigger={
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ marginBottom: 24 }}
                        />
                      }
                    />
                  </Flex>
                ))}
                {/* <SBRM.Actions
                actions={[SBRMAction.Create]}
                entity={Entity.membershipProfileCurrency}
                metadata={[
                  {
                    key: MetadataKey.MembershipProfileId,
                    value: membershipProfile.id,
                  },
                ]}
                trigger={
                  <Button type="dashed" block icon={<PlusOutlined />}>
                    Add new balance
                  </Button>
                }
              /> */}
              </Card>
            </Flex>
          </Form>
        </Col>
        <Col span={24}>
          <Form
            form={creditForm}
            layout="vertical"
            name="credit"
            onFinish={async (values: any) => {
              const quantities = values.quantity
              if (!quantities) return

              const items = complementaryItems
                .filter((item) => quantities[item.id] !== undefined)
                .map((item) => ({ id: item.id, quantity: quantities[item.id] }))

              if (!items.length) return

              setIsCreditSaving(true)
              try {
                await batchUpdateMembershipProfileComplementaryItems(
                  membershipProfile.id,
                  items
                )
                fetchMembershipProfileDetails()
                message.success(DEFAULT_SUCCESS)
              } catch {
                message.error(DEFAULT_ERROR)
              } finally {
                setIsCreditSaving(false)
              }
            }}
          >
            <Flex vertical className="w-full" gap={8}>
              <Space className="w-full space-between">
                <Title level={5} className="m-0">
                  Credits
                </Title>
                <Space>
                  <Button
                    style={{ border: 0 }}
                    type="primary"
                    htmlType="submit"
                    loading={isCreditSaving}
                    disabled={isCreditSaving}
                  >
                    Save
                  </Button>
                </Space>
              </Space>

              <Card>
                {complementaryItems.map((item) => (
                  <Flex key={item.id} align="flex-end" gap={8}>
                    <div style={{ flex: 1 }}>
                      <ComplementaryItem.Spinner
                        entity="membershipProfile"
                        id={item.id}
                      />
                    </div>
                    <SBRM.Actions
                      actions={[SBRMAction.Delete]}
                      entity={Entity.membershipProfileComplementaryItem}
                      entityId={item.id}
                      trigger={
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          style={{ marginBottom: 24 }}
                        />
                      }
                    />
                  </Flex>
                ))}
                {/* <SBRM.Actions
                actions={[SBRMAction.Create]}
                entity={Entity.membershipProfileComplementaryItem}
                metadata={[
                  {
                    key: MetadataKey.MembershipProfileId,
                    value: membershipProfile?.id,
                  },
                ]}
                trigger={
                  <Button type="dashed" block icon={<PlusOutlined />}>
                    Create new item
                  </Button>
                }
              /> */}
              </Card>
            </Flex>
          </Form>
        </Col>
      </Row>
    </Localizable>
  )
}

type ComplementaryItemSectionType = {
  ComplementaryItemSection: typeof ComplementaryItemSection
}
export { ComplementaryItemSection, type ComplementaryItemSectionType }
